const BYTES_PER_MEGABYTE = 1024 * 1024;
const EMAIL_IMAGE_MAX_SOURCE_BYTES = 20 * BYTES_PER_MEGABYTE;
const EMAIL_IMAGE_MAX_OUTPUT_BYTES = 1.25 * BYTES_PER_MEGABYTE;
const EMAIL_IMAGE_MAX_DIMENSION = 1600;

const imageMimeTypeByExtension: Record<string, string> = {
  avif: "image/avif",
  bmp: "image/bmp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  svg: "image/svg+xml",
  webp: "image/webp",
};

const transparencyCapableImageTypes = new Set([
  "image/gif",
  "image/png",
  "image/svg+xml",
]);

export const emailImageAccept = "image/*";

const getFileExtension = (fileName: string): string =>
  fileName.split(".").pop()?.trim().toLowerCase() || "";

const getSourceImageMimeType = ({ file }: { file: File }): string => {
  const browserMimeType = file.type.trim().toLowerCase();

  if (browserMimeType.startsWith("image/")) {
    return browserMimeType;
  }

  return imageMimeTypeByExtension[getFileExtension(file.name)] || "";
};

export const isEmailImageFile = ({ file }: { file: File }): boolean =>
  !!getSourceImageMimeType({ file });

const withSourceImageMimeType = ({
  file,
  sourceMimeType,
}: {
  file: File;
  sourceMimeType: string;
}): File =>
  file.type === sourceMimeType
    ? file
    : new File([file], file.name, {
        lastModified: file.lastModified,
        type: sourceMimeType,
      });

const getEmailSafeImageFileName = ({
  fileName,
  outputMimeType,
}: {
  fileName: string;
  outputMimeType: "image/jpeg" | "image/png";
}): string => {
  const baseName = fileName.replace(/\.[^.]+$/, "").trim() || "email-image";
  return `${baseName}.${outputMimeType === "image/png" ? "png" : "jpg"}`;
};

export const prepareEmailAssetForUpload = async ({
  file,
}: {
  file: File;
}): Promise<File> => {
  const sourceMimeType = getSourceImageMimeType({ file });

  // PDFs and other allowed email attachments use the same presigned uploader,
  // but only visual assets should be rasterized.
  if (!sourceMimeType) {
    return file;
  }

  if (file.size <= 0) {
    throw new Error("The selected image is empty.");
  }

  if (file.size > EMAIL_IMAGE_MAX_SOURCE_BYTES) {
    throw new Error("The source image must be 20MB or smaller.");
  }

  const outputMimeType: "image/jpeg" | "image/png" =
    transparencyCapableImageTypes.has(sourceMimeType)
      ? "image/png"
      : "image/jpeg";
  const sourceImageFile = withSourceImageMimeType({ file, sourceMimeType });

  try {
    const { default: imageCompression } = await import(
      "browser-image-compression"
    );
    const normalizedImage = await imageCompression(sourceImageFile, {
      maxSizeMB: 1,
      maxWidthOrHeight: EMAIL_IMAGE_MAX_DIMENSION,
      maxIteration: 15,
      initialQuality: 0.86,
      fileType: outputMimeType,
      preserveExif: false,
      // Avoid the library's CDN-backed worker. The bundled main-thread path is
      // deterministic under the dashboard's Content Security Policy.
      useWebWorker: false,
    });

    if (
      normalizedImage.size <= 0 ||
      normalizedImage.size > EMAIL_IMAGE_MAX_OUTPUT_BYTES
    ) {
      throw new Error("The optimized image is still too large for email.");
    }

    return new File(
      [normalizedImage],
      getEmailSafeImageFileName({
        fileName: file.name,
        outputMimeType,
      }),
      {
        lastModified: file.lastModified,
        type: outputMimeType,
      }
    );
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === "The optimized image is still too large for email."
    ) {
      throw error;
    }

    throw new Error(
      "This image could not be converted for email. Try exporting it as a PNG or JPG."
    );
  }
};
